
import React, { useEffect, useState } from 'react';
import { Card, Button, Input, Badge } from '../components/UI';
import { apiRequest } from '../utils/api';
import { Trophy, Award, Shield, Star, Plus, Trash2, Save, UserPlus, CheckCircle, Zap, Edit2, X, List, Grid, Calendar, User } from 'lucide-react';
import { useLeague } from '../components/LeagueContext';
import { API_BASE_URL } from '../config';

interface BadgeItem {
    id: string;
    name: string;
    icon: string;
    color: string;
    description: string;
    assigned_count: number;
}

interface BadgeAssignment {
    user_id: string;
    badge_id: string;
    username: string;
    avatar_url?: string;
    badge_name: string;
    icon: string;
    color: string;
    earned_at: string;
}

export const BadgeAdmin: React.FC = () => {
    const { drivers, settings, refreshSettings } = useLeague();
    const [badges, setBadges] = useState<BadgeItem[]>([]);
    const [assignments, setAssignments] = useState<BadgeAssignment[]>([]);
    const [newBadge, setNewBadge] = useState<Partial<BadgeItem>>({ name: '', icon: 'Trophy', color: 'yellow', description: '' });
    const [assignData, setAssignData] = useState({ badgeId: '', userId: '' });
    const [isEditing, setIsEditing] = useState(false);
    const [activeTab, setActiveTab] = useState<'MANAGE' | 'ASSIGNMENTS'>('MANAGE');
    
    // DotD Selection
    const [dotdBadgeId, setDotdBadgeId] = useState<string>('');

    useEffect(() => {
        loadBadges();
        if(settings.dotd_badge_id) setDotdBadgeId(settings.dotd_badge_id.toString());
    }, [settings]);

    useEffect(() => {
        if (activeTab === 'ASSIGNMENTS') {
            loadAssignments();
        } else {
            loadBadges(); // Refresh counts when going back
        }
    }, [activeTab]);

    const loadBadges = () => {
        // Add cache buster
        apiRequest<BadgeItem[]>(`badges.php?_t=${Date.now()}`).then(data => setBadges(data || []));
    };

    const loadAssignments = () => {
        // Add cache buster
        apiRequest<BadgeAssignment[]>(`badges.php?mode=assignments&_t=${Date.now()}`).then(data => {
            if(Array.isArray(data)) setAssignments(data);
            else setAssignments([]);
        });
    };

    const handleSave = async () => {
        if (!newBadge.name) return;
        try {
            await apiRequest('badges.php', 'POST', newBadge);
            loadBadges();
            setNewBadge({ name: '', icon: 'Trophy', color: 'yellow', description: '' });
            setIsEditing(false);
        } catch (e: any) {
            alert(e.message);
        }
    };

    const handleEdit = (badge: BadgeItem) => {
        setNewBadge(badge);
        setIsEditing(true);
        setActiveTab('MANAGE');
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleCancelEdit = () => {
        setNewBadge({ name: '', icon: 'Trophy', color: 'yellow', description: '' });
        setIsEditing(false);
    };

    const handleDeleteBadge = async (id: string) => {
        if (!confirm("Badge wirklich löschen? Dies entfernt es auch von allen Fahrern!")) return;
        await apiRequest(`badges.php?id=${id}`, 'DELETE');
        loadBadges();
    };

    const handleDeleteAssignment = async (userId: string, badgeId: string) => {
        if (!confirm("Badge von diesem Fahrer entfernen?")) return;
        try {
            await apiRequest(`badges.php?userId=${userId}&id=${badgeId}`, 'DELETE');
            // Refresh logic
            const newAssignments = assignments.filter(a => !(a.user_id === userId && a.badge_id === badgeId));
            setAssignments(newAssignments);
            // Double check load
            setTimeout(loadAssignments, 500);
        } catch (e: any) {
            alert(e.message);
        }
    };

    const handleAssign = async () => {
        if (!assignData.userId || !assignData.badgeId) return;
        try {
            await apiRequest('badges.php', 'POST', { action: 'assign', ...assignData });
            alert("Badge vergeben!");
            
            // Clear User selection
            setAssignData({ ...assignData, userId: '' }); 
            
            // Reload both lists to be safe
            loadBadges();
            if(activeTab === 'ASSIGNMENTS') loadAssignments();
        } catch (e: any) {
            alert(e.message);
        }
    };

    const saveDotdSetting = async () => {
        try {
            await apiRequest('settings.php', 'PUT', { dotd_badge_id: dotdBadgeId });
            await refreshSettings();
            alert("DotD Badge Einstellung gespeichert!");
        } catch(e: any) {
            alert("Error: " + e.message);
        }
    };

    const getUrl = (url?: string) => {
        if (!url) return undefined;
        if (url.startsWith('http') || url.startsWith('data:')) return url;
        const baseUrl = API_BASE_URL === '.' ? '' : API_BASE_URL;
        return baseUrl ? `${baseUrl}/${url}` : url;
    };

    const renderIcon = (name: string, className = "w-6 h-6") => {
        if (name === 'Trophy') return <Trophy className={className}/>;
        if (name === 'Award') return <Award className={className}/>;
        if (name === 'Shield') return <Shield className={className}/>;
        return <Star className={className}/>;
    };

    return (
        <div className="space-y-6 animate-fade-in pb-10">
            <div className="flex justify-between items-center">
                <div>
                    <h1 className="text-2xl font-bold uppercase italic flex items-center gap-3">
                <Award size={32} className="text-f1-red"/> Badge-Verwaltung
                    </h1>
                </div>
                <div className="flex bg-gray-800 rounded p-1">
                    <button 
                        onClick={() => setActiveTab('MANAGE')} 
                        className={`px-4 py-2 rounded text-xs font-bold uppercase flex items-center gap-2 transition-colors ${activeTab==='MANAGE'?'bg-f1-red text-white':'text-gray-400 hover:text-white'}`}
                    >
                        <Grid size={14}/> Verwaltung
                    </button>
                    <button 
                        onClick={() => setActiveTab('ASSIGNMENTS')} 
                        className={`px-4 py-2 rounded text-xs font-bold uppercase flex items-center gap-2 transition-colors ${activeTab==='ASSIGNMENTS'?'bg-f1-red text-white':'text-gray-400 hover:text-white'}`}
                    >
                        <List size={14}/> Vergebene Badges
                    </button>
                </div>
            </div>

            {activeTab === 'MANAGE' ? (
                <div className="space-y-6">
                    {/* DOTD Config */}
                    <Card className="border-l-4 border-l-yellow-500">
                        <div className="flex flex-col md:flex-row justify-between items-center gap-4">
                            <div className="flex items-center gap-3">
                                <Zap className="text-yellow-500 w-8 h-8" />
                                <div>
                                    <h3 className="font-bold text-lg uppercase text-white">Driver of the Day Automation</h3>
                                    <p className="text-gray-400 text-sm">Wähle das Badge, das dem DotD-Gewinner automatisch verliehen wird (nach Ablauf des Votings).</p>
                                </div>
                            </div>
                            <div className="flex items-center gap-2">
                                <select 
                                    className="bg-gray-900 border border-gray-700 rounded p-2 text-white min-w-[200px]"
                                    value={dotdBadgeId}
                                    onChange={e => setDotdBadgeId(e.target.value)}
                                >
                                    <option value="">-- Wähle DotD Badge --</option>
                                    {badges.map(b => (
                                        <option key={b.id} value={b.id}>{b.name}</option>
                                    ))}
                                </select>
                                <Button onClick={saveDotdSetting} disabled={!dotdBadgeId}>Speichern</Button>
                            </div>
                        </div>
                    </Card>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {/* Create/Edit Badge */}
                        <Card title={isEditing ? "Badge bearbeiten" : "Neues Badge erstellen"}>
                            <div className="space-y-4">
                                <Input label="Name" value={newBadge.name} onChange={e => setNewBadge({...newBadge, name: e.target.value})} placeholder="z.B. Season 1 Winner"/>
                                <Input label="Beschreibung" value={newBadge.description} onChange={e => setNewBadge({...newBadge, description: e.target.value})}/>
                                
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-gray-400 text-xs font-bold mb-2 uppercase">Icon</label>
                                        <select className="w-full bg-gray-900 border border-gray-700 rounded p-2 text-white" value={newBadge.icon} onChange={e => setNewBadge({...newBadge, icon: e.target.value})}>
                                            <option value="Trophy">Pokal</option>
                                            <option value="Award">Award</option>
                                            <option value="Star">Stern</option>
                                            <option value="Shield">Schild</option>
                                        </select>
                                    </div>
                                    <div>
                                        <label className="block text-gray-400 text-xs font-bold mb-2 uppercase">Farbe</label>
                                        <select className="w-full bg-gray-900 border border-gray-700 rounded p-2 text-white" value={newBadge.color} onChange={e => setNewBadge({...newBadge, color: e.target.value})}>
                                            <option value="yellow">Gold / Gelb</option>
                                            <option value="gray">Silber / Grau</option>
                                            <option value="orange">Bronze / Orange</option>
                                            <option value="blue">Blau</option>
                                            <option value="red">Rot</option>
                                        </select>
                                    </div>
                                </div>
                                <div className="pt-2 flex gap-2">
                                    <Button onClick={handleSave} icon={Save} disabled={!newBadge.name}>{isEditing ? 'Änderungen speichern' : 'Badge erstellen'}</Button>
                                    {isEditing && <Button variant="ghost" onClick={handleCancelEdit}><X className="mr-2"/> Abbrechen</Button>}
                                </div>
                            </div>
                        </Card>

                        {/* Assign Badge */}
                        <Card title="Badge manuell vergeben">
                            <div className="space-y-4">
                                <div>
                                    <label className="block text-gray-400 text-xs font-bold mb-2 uppercase">Badge wählen</label>
                                    <select className="w-full bg-gray-900 border border-gray-700 rounded p-2 text-white" value={assignData.badgeId} onChange={e => setAssignData({...assignData, badgeId: e.target.value})}>
                                        <option value="">-- Wählen --</option>
                                        {badges.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-gray-400 text-xs font-bold mb-2 uppercase">Fahrer wählen</label>
                                    <select className="w-full bg-gray-900 border border-gray-700 rounded p-2 text-white" value={assignData.userId} onChange={e => setAssignData({...assignData, userId: e.target.value})}>
                                        <option value="">-- Wählen --</option>
                                        {drivers.map(d => <option key={d.id} value={d.id}>{d.username}</option>)}
                                    </select>
                                </div>
                                <div className="pt-2">
                                    <Button variant="secondary" onClick={handleAssign} icon={UserPlus} disabled={!assignData.userId || !assignData.badgeId}>An Fahrer vergeben</Button>
                                </div>
                            </div>
                        </Card>
                    </div>

                    {/* List */}
                    <div className="bg-gray-800 border border-gray-700 rounded p-4">
                        <h3 className="text-white font-bold mb-4 uppercase">Vorhandene Badges</h3>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            {badges.map(b => (
                                <div key={b.id} className="bg-gray-900 p-3 rounded flex items-center justify-between border border-gray-800 hover:border-gray-600 transition-colors">
                                    <div className="flex items-center gap-3">
                                        <div className={`w-10 h-10 rounded-full flex items-center justify-center bg-${b.color}-900/20 text-${b.color}-500 border border-${b.color}-500/50`}>
                                            {renderIcon(b.icon)}
                                        </div>
                                        <div>
                                            <div className="font-bold text-white text-sm flex items-center gap-2">
                                                {b.name}
                                                {b.id === dotdBadgeId && <span className="text-[10px] bg-yellow-500/20 text-yellow-500 border border-yellow-500/50 px-1 rounded">DOTD</span>}
                                            </div>
                                            <div className="text-xs text-gray-500">{b.assigned_count}x vergeben</div>
                                        </div>
                                    </div>
                                    <div className="flex gap-1">
                                        <button onClick={() => handleEdit(b)} className="text-gray-500 hover:text-white p-2 rounded hover:bg-gray-800 transition-colors" title="Bearbeiten">
                                            <Edit2 size={16}/>
                                        </button>
                                        <button onClick={() => handleDeleteBadge(b.id)} className="text-gray-500 hover:text-red-500 p-2 rounded hover:bg-gray-800 transition-colors" title="Löschen">
                                            <Trash2 size={16}/>
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            ) : (
                <div className="bg-[#1e1e24] border border-gray-700 rounded-lg overflow-hidden shadow-lg">
                    {assignments.length === 0 ? (
                         <div className="p-8 text-center text-gray-500 italic flex flex-col items-center gap-2">
                             <Award className="opacity-20" size={48}/>
                             Keine Badges vergeben. Nutze den "Verwaltung"-Tab um Badges zuzuweisen.
                         </div>
                    ) : (
                        <div className="overflow-x-auto">
                        <table className="w-full text-left text-sm">
                            <thead className="bg-gray-900 text-gray-400 uppercase text-xs font-bold">
                                <tr>
                                    <th className="p-4">Fahrer</th>
                                    <th className="p-4">Badge</th>
                                    <th className="p-4">Erhalten am</th>
                                    <th className="p-4 text-right">Aktion</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-800">
                                {assignments.map((assign, idx) => (
                                    <tr key={`${assign.user_id}_${assign.badge_id}_${idx}`} className="hover:bg-gray-800/50 transition-colors">
                                        <td className="p-4">
                                            <div className="flex items-center gap-3">
                                                <div className="w-8 h-8 rounded-full bg-gray-800 border border-gray-600 overflow-hidden flex items-center justify-center">
                                                    {assign.avatar_url ? <img src={getUrl(assign.avatar_url)} className="w-full h-full object-cover"/> : <User size={14} className="text-gray-400"/>}
                                                </div>
                                                <span className="font-bold text-white">{assign.username}</span>
                                            </div>
                                        </td>
                                        <td className="p-4">
                                            <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-${assign.color}-900/20 text-${assign.color}-400 border border-${assign.color}-900/50`}>
                                                {renderIcon(assign.icon, "w-4 h-4")}
                                                <span className="font-bold text-xs uppercase">{assign.badge_name}</span>
                                            </div>
                                        </td>
                                        <td className="p-4 text-gray-500 text-xs font-mono">
                                            {new Date(assign.earned_at).toLocaleDateString()} <span className="text-[10px] opacity-60 ml-1">{new Date(assign.earned_at).toLocaleTimeString()}</span>
                                        </td>
                                        <td className="p-4 text-right">
                                            <button 
                                                onClick={() => handleDeleteAssignment(assign.user_id, assign.badge_id)} 
                                                className="p-2 bg-red-900/10 text-red-500 rounded hover:bg-red-900/30 border border-red-900/30 transition-colors"
                                                title="Entfernen"
                                            >
                                                <Trash2 size={16}/>
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};
